<template>
    <div v-cloak>
        <v-progress-linear v-if="Loading" indeterminate></v-progress-linear>
        <v-card v-if="loadedStudent && HasStudentID === false" flat>
            <v-card-text>
                <v-row>
                    <v-col cols="auto">
                        <v-alert type="error" outlined dense text><span v-html="`Student or course application not found`"></span></v-alert>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>
        <v-row v-if="loadedStudent && HasStudentID === true">
            <v-col cols="12" md="9">
                <v-card flat>
                    <v-card-text>
                        <h1 class="mb-4">Hi {{ student.Forenames }}</h1>
                        <h3 class="mb-4" style="font-weight:normal">Welcome to the Dundee and Angus College Student Funding Application system</h3>
                        <p>All communication in relation to your applications will be sent via email.  Copies of these emails will be shown in your inbox.  Click on your name in the top-right-hand corner to access your inbox.</p>
                        <p class="mb-0">If you would like further information on funding while you are studying at College, please click <a href="https://dundeeandangus.ac.uk/studying-at-da/fees-finance-funding/" target="_blank">Fees, Finance &amp; Funding</a>.</p>
                    </v-card-text>
                    <v-card-text v-if="IsStudentOldEnough === false">
                        <v-row>
                            <v-col cols="auto">
                                <v-alert type="warning" outlined dense text><span v-html="`Due to your age, you are not eligible for College funding at this time - please contact your school to discuss your available options`"></span></v-alert>
                            </v-col>
                        </v-row>
                    </v-card-text>
                    <v-card-text v-if="HasBursary">
                        <v-card elevation="4" class="mb-4">
                            <v-toolbar color="primary" flat dark dense>
                                <v-toolbar-title>Bursaries</v-toolbar-title>
                                <v-spacer></v-spacer>
                            </v-toolbar>
                            <v-data-table :headers="bursaryHeaders"
                                          :items="bursaries"
                                          :item-class="() => 'selectable-row'"
                                          :loading="LaodingBursaries"
                                          sort-by="awardOfferedDate"
                                          @click:row="OpenBursary"
                                          hide-default-footer>
                                <template v-slot:item.awardOfferedDate="{ item }">
                                    {{ item.awardOfferedDate | formatDate }}
                                </template>
                                <template v-slot:item.payAward="{ item }">
                                    <v-progress-circular v-if="LoadingBursaries" indeterminate></v-progress-circular>
                                    <span v-else>&pound;{{ GetTotalPayAward(item) | formatCurrency }}</span>
                                </template>
                                <template v-slot:item.startDate="{ item }">
                                    {{ item.startDate | formatDate }}
                                </template>
                                <template v-slot:item.endDate="{ item }">
                                    {{ item.endDate | formatDate }}
                                </template>
                                <template v-slot:item.actions="{ item }">
                                    <v-btn @click.stop="OpenBursary(item)"
                                           color="info"
                                           dark
                                           small>
                                        View
                                        <v-icon right>fas fa-chevron-right</v-icon>
                                    </v-btn>
                                </template>
                            </v-data-table>
                        </v-card>
                    </v-card-text>
                    <v-card-text v-if="IsStudentOldEnough === true">
                        <v-card elevation="4" class="mb-4">
                            <v-toolbar color="primary" flat dark dense>
                                <v-toolbar-title>Applications</v-toolbar-title>
                                <v-spacer></v-spacer>
                            </v-toolbar>
                            <v-progress-linear v-if="Loading" color="primary" indeterminate></v-progress-linear>
                            <v-card-text v-if="HasAnyCourses === true && currentDocumentProcessingDate != null">
                                <v-row>
                                    <v-col cols="auto">
                                        <v-alert type="info" outlined dense text class="ma-0"><span v-html="`We are currently processing application Bursary and EMA documents submitted on <strong>${this.$options.filters.formatLongDate(currentDocumentProcessingDate)}</strong>`"></span></v-alert>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                            <v-card-text v-if="HasAnyCourses === false">
                                <v-row>
                                    <v-col cols="auto">
                                        <v-alert type="info" outlined dense text class="ma-0">You have no applications eligible for funding at this time</v-alert>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                            <v-card-text>
                                <p>Below are the applications you have made through this system to date.</p>
                                <p>You can view or edit your applications by clicking on the <strong>Open</strong> button beside the associated application. You can view and upload documents to your submitted application by clicking on the <strong>View</strong> button beside the application. <strong>You can only edit unsubmitted applications.</strong></p>
                                <p>If you wish to delete an application, click <strong>Delete</strong>.  <strong>You can only delete unsubmitted applications.</strong></p>
                            </v-card-text>
                            <v-card-text v-for="(courseApplication, index) in courseApplications"
                                         :key="courseApplication.CourseCode"
                                         v-if="CanShowCourse(courseApplication)">
                                <v-card outlined>
                                    <v-card-title>
                                        <span>{{ courseApplication.CourseName }}</span>
                                        <v-spacer></v-spacer>
                                        <span>{{ courseApplication.CourseCode }}</span>
                                    </v-card-title>
                                    <v-card-text v-if="IsCourseApplicationCancelled(courseApplication) === true">
                                        <v-row>
                                            <v-col cols="auto">
                                                <v-alert type="error" outlined dense text>Your course application has been cancelled</v-alert>
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                    <v-card-text v-else-if="IsCourseApplicationValid(courseApplication) === false">
                                        <v-row>
                                            <v-col cols="auto">
                                                <v-alert type="error" outlined dense text>Your course application does not have a valid status to allow you to apply for funding yet</v-alert>
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                    <v-card-text v-for="(form, index) in GetFormsByCourseApplication(courseApplication)"
                                                 :key="form.id"
                                                 v-if="CanShowForm(courseApplication, form.type)">
                                        <v-card outlined>
                                            <v-card-title>
                                                <span>{{ form.name }}</span>
                                                <v-spacer></v-spacer>
                                                <v-btn v-if="CanApply(courseApplication, form.type)"
                                                       @click="OpenNewApplication(courseApplication, form.type)"
                                                       color="success"
                                                       dark>
                                                    Apply for {{ form.name }} Funding
                                                    <v-icon right>fas fa-chevron-right</v-icon>
                                                </v-btn>
                                            </v-card-title>
                                            <v-card-text v-if="form.formContent?.length > 0">
                                                <div v-html="TranslatePlaceholders(objectSettings.filter(s => s.objectType == 'Form' && s.objectId == form.id), form.formContent)"></div>
                                            </v-card-text>
                                            <v-card-text>
                                                <v-row v-if="HasApplicationDates(courseApplication, form.type)">
                                                    <v-col cols="auto">
                                                        <v-alert type="info" outlined dense text><span v-html="GetApplicationOpenDatesLabel(courseApplication, form)"></span></v-alert>
                                                    </v-col>
                                                </v-row>
                                            </v-card-text>
                                            <v-divider v-if="HasApplicationByCourseFormType(courseApplication.CourseCode, form.type)"></v-divider>
                                            <v-data-table v-if="HasApplicationByCourseFormType(courseApplication.CourseCode, form.type)"
                                                          :headers="form.type == 'EMA' ? emaApplicationHeaders : applicationHeaders"
                                                          :items="GetApplicationsByCourseFormType(courseApplication.CourseCode, form.type)"
                                                          :item-class="() => 'selectable-row'"
                                                          :loading="loadingApplications"
                                                          @click:row="application => OpenApplication(application, courseApplication)"
                                                          hide-default-footer>
                                                <template v-slot:item.status="{ item }">
                                                    <v-chip label :color="GetApplicationStatusColor(item.status)" dark small>{{ item.status }}</v-chip>
                                                </template>
                                                <template v-slot:item.lastUpdated="{ item }">
                                                    {{ item.lastUpdated | formatDateTime }}
                                                </template>
                                                <template v-slot:item.numberOfMissingChecklistItems="{ item }">
                                                    <v-icon v-if="item.numberOfMissingChecklistItems == 0" color="success" left>fas fa-check-circle</v-icon>
                                                    <v-icon v-else color="error" left>fas fa-exclamation-triangle</v-icon>
                                                    {{ item.numberOfMissingChecklistItems }} Files Required
                                                </template>
                                                <template v-slot:item.emaLearnerAgreement="{ item }">
                                                    <v-checkbox v-if="item.formType == 'EMA' && item.isCareExperienced === false"
                                                                label="Accept the EMA terms and conditions?"
                                                                v-model="item.learningAgreement"
                                                                :disabled="HasEligibleEMABursary(item) === false"
                                                                @click.stop=""
                                                                @change="UpdateLearningAgreement(item)"></v-checkbox>
                                                </template>
                                                <template v-slot:item.actions="{ item }">
                                                    <v-btn @click.stop="OpenApplication(item, courseApplication)"
                                                           :disabled="IsCourseApplicationCancelled(courseApplication)"
                                                           :color="IsCourseApplicationCancelled(courseApplication) ? '' : 'info'"
                                                           :dark="!IsCourseApplicationCancelled(courseApplication)"
                                                           small>
                                                        {{ OpenApplicationLabel(item) }}
                                                        <v-icon right>fas fa-chevron-right</v-icon>
                                                    </v-btn>
                                                    <v-btn v-if="IsApplicationUnsubmitted(item)"
                                                           @click.stop="ConfirmDeleteApplication(item)"
                                                           color="error"
                                                           dark
                                                           small
                                                           class="ml-2">
                                                        Delete
                                                        <v-icon right>fas fa-chevron-right</v-icon>
                                                    </v-btn>
                                                </template>
                                            </v-data-table>
                                        </v-card>
                                    </v-card-text>
                                </v-card>
                            </v-card-text>
                        </v-card>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="12" md="3">
                <right-navigation></right-navigation>
            </v-col>
        </v-row>

        <confirm-dialog instance="confirm-delete-application"
                        title="Confirm Delete Application"
                        question="Are you sure you want to delete this application?"
                        button-color="error"></confirm-dialog>
    </div>
</template>

<script>
    import moment from 'moment'
    import { eventBus } from '@/main'

    import filtersMixin from '@/views/components/mixins/base/filters.js'
    import rulesMixin from '@/views/components/mixins/base/rules.js'
    import ctrlOpenMixin from '@/views/components/mixins/base/ctrlOpen.js'
    import appApiMixin from '@/views/components/mixins/appApi.js'
    import localSettingsMixin from '@/views/components/mixins/localSettings.js'
    import constantsMixin from '@/views/components/mixins/enterpriseDataApi/constants.js'
    import studentMixin from '@/views/components/mixins/enterpriseDataApi/students.js'
    import formMixin from '@/views/components/mixins/forms.js'
    import objectSettingsMixin from '@/views/components/mixins/objectSettings.js'
    import applicantMixin from '@/views/components/mixins/enterpriseDataApi/applicants.js'
    import applicationMixin from '@/views/components/mixins/applications/applications.js'
    import communicationMixin from '@/views/components/mixins/communications.js'
    import bursaryMixin from '@/views/components/mixins/bursaries.js'
    import courseMixin from '@/views/components/mixins/courses.js'
    import placeholderMixin from '@/views/components/mixins/controls/placeholders.js'
    import yearMixin from '@/views/components/mixins/years.js'
    import formApplicationMixin from '@/views/components/mixins/applications/formApplications.js'

    import rightNavigation from '@/views/components/panels/RightNavigation'
    import confirmDialog from '@/views/components/dialogs/ConfirmDialog'

    export default {
        mixins: [
            filtersMixin,
            rulesMixin,
            ctrlOpenMixin,
            appApiMixin,
            localSettingsMixin,
            constantsMixin,
            studentMixin,
            formMixin,
            objectSettingsMixin,
            applicantMixin,
            applicationMixin,
            communicationMixin,
            bursaryMixin,
            courseMixin,
            placeholderMixin,
            yearMixin,
            formApplicationMixin,
        ],
        components: {
            'right-navigation': rightNavigation,
            'confirm-dialog': confirmDialog,
        },
        data() {
            return {
                appSettings: {},

                student: null,
                loadingStudent: false,
                loadedStudent: false,

                loadingCourseApplications: false,
                loadedApplicantCourses: false,
                courseApplications: [],
                courses: [],

                year: null,
                forms: [],
                objectSettings: [],

                loadingApplications: false,
                applications: [],
                applicationHeaders: [
                    { text: 'Application Reference', value: 'applicationReference' },
                    { text: 'Status', value: 'status' },
                    { text: 'Checklist Complete?', value: 'numberOfMissingChecklistItems' },
                    { text: 'Last Updated', value: 'lastUpdated' },
                    { text: 'Actions', value: 'actions', width: 220 },
                ],
                emaApplicationHeaders: [
                    { text: 'Application Reference', value: 'applicationReference' },
                    { text: 'Status', value: 'status' },
                    { text: 'Checklist Complete?', value: 'numberOfMissingChecklistItems' },
                    { text: 'Last Updated', value: 'lastUpdated' },
                    { text: 'EMA Learner Agreement', value: 'emaLearnerAgreement' },
                    { text: 'Actions', value: 'actions', width: 220 },
                ],

                loadingCommunications: false,
                loadedCommunications: false,
                communications: [],
                communicationHeaders: [
                    { text: 'Format', value: 'format' },
                    { text: 'Subject', value: 'subject', width: '50%' },
                    { text: 'Status', value: 'status' },
                    { text: 'Reference', value: 'reference' },
                    { text: 'Last Updated', value: 'eventTime' },
                    { text: 'Actions', value: 'actions', width: 120 },
                ],

                loadingBursaries: false,
                bursaries: [],
                loadingBursaryFundingPaymentDetails: false,
                bursaryFundingPaymentDetails: [],
                bursaryHeaders: [
                    { text: 'Reference', value: 'bursaryReference' },
                    { text: 'Date Awarded', value: 'awardOfferedDate' },
                    { text: 'Total Award', value: 'payAward' },
                    { text: 'Start Date', value: 'startDate' },
                    { text: 'End Date', value: 'endDate' },
                    { text: 'Actions', value: 'actions', width: 120 },
                ],
            }
        },
        computed: {
            IsBetaUITester() { return this.appSettings?.betaUITesting === true; },
            Loading() { return this.loadingStudent || this.loadingCourseApplications || this.loadingApplications; },
            LoadingBursaries() { return this.loadingBursaries || this.loadingBursaryFundingPaymentDetails },
            HasStudentID() { return this.student?.StudentID != null; },
            IsStudentOldEnough() {
                if (moment(this.student?.DateOfBirth) > moment(this.year?.xmasLeaversEndDate)) return false;
                else return true;
            },
            LatestCourseApplicationAcademicSession() { return Math.max(...this.courseApplications.map(ca => ca.AcademicSession)); },
            HasAnyCourses() {
                for (let i = 0; i < this.courseApplications.length; i++) {
                    if (this.HasCourse(this.courseApplications[i]) === true) return true;
                };
                return false;
            },
            HasApplications() { return this.applications.length > 0; },
            ActiveApplications() { return this.applications.filter(a => ['Cancelled', 'Funding Not Requested'].includes(a.status) === false) || []; },
            SubmittedApplications() { return this.applications.filter(a => ['Submitted', 'Documents Requested', 'Admin Checked'].includes(a.status) === true) || []; },
            HasCommunications() { return this.communications.length > 0; },
            FormBody() { return this.TranslatePlaceholders(this.form, this.form?.body); },
            HasBursary() { return this.IsBetaUITester && this.bursaries.length > 0; },
        },
        mounted() {
            eventBus.$off('constants-fetch:success').$on('constants-fetch:success', () => {
                this.LoadAllObjectSettings();
                this.LoadStudentByKeycloakID();
                this.LoadApplications();
            });

            eventBus.$off('student-fetch:started').$on('student-fetch:started', () => { this.loadingStudent = true; this.loadedStudent = false; });
            eventBus.$off('student-fetch:success').$on('student-fetch:success', () => {
                if (this.HasStudentID) {
                    this.LoadCurrentCourseApplicationsByStudentID();
                    this.LoadCommunicationsByStudentID();
                    this.LoadBursaries();
                }
            });
            eventBus.$off('student-fetch:finished').$on('student-fetch:finished', () => { this.loadingStudent = false; this.loadedStudent = true; });

            eventBus.$off('course-applications-fetch:success').$on('course-applications-fetch:success', () => {
                this.LoadYearByAcademicSession(this.LatestCourseApplicationAcademicSession);
                this.LoadCoursesByCourseCodes(this.courseApplications.map(ac => ac.CourseCode));
            });

            eventBus.$off('applications-fetch:started').$on('applications-fetch:started', () => this.loadingApplications = true);
            eventBus.$off('applications-fetch:finished').$on('applications-fetch:finished', () => this.loadingApplications = false);

            eventBus.$off('communications-fetch:started').$on('communications-fetch:started', () => { this.loadingCommunications = true; this.loadedCommunications = false; });
            eventBus.$off('communications-fetch:finished').$on('communications-fetch:finished', () => { this.loadingCommunications = false; this.loadedCommunications = true; });

            eventBus.$off('confirm-dialog:confirm-delete-application:ok').$on('confirm-dialog:confirm-delete-application:ok', application => this.DeleteApplication(application));
            eventBus.$off('application-delete:success').$on('application-delete:success', () => this.LoadApplications());

            eventBus.$off('bursaries-fetch:started').$on('bursaries-fetch:started', () => this.loadingBursaries = true);
            eventBus.$off('bursaries-fetch:success').$on('bursaries-fetch:success', () => {
                this.LoadBursaryFundingPaymentDetailsByBursaryIDs(this.bursaries.map(b => b.id));
            });
            eventBus.$off('bursaries-fetch:finished').$on('bursaries-fetch:finished', () => this.loadingBursaries = false);

            eventBus.$off('bursary-funding-payment-details-fetch:started').$on('bursary-funding-payment-details-fetch:started', () => this.loadingBursaryFundingPaymentDetails = true);
            eventBus.$off('bursary-funding-payment-details-fetch:finished').$on('bursary-funding-payment-details-fetch:finished', () => this.loadingBursaryFundingPaymentDetails = false);

            this.LoadAppSettings();
            this.LoadConstants();
            this.LoadLocalSettings();
            this.LoadForms();
        },
        methods: {
            HasCourse(courseApplication) { return this.courses.some(c => c.courseCode == courseApplication.CourseCode && c.authorised === true); },
            IsCourseApplicationCancelled(courseApplication) { return courseApplication.ApplicationStatusCode == 'CANC'; },
            IsCourseApplicationValid(courseApplication) { return courseApplication.IsOfferAccepted === true || courseApplication.IsPreRegistered === true || courseApplication.IsRegistered === true || courseApplication.ApplicationStatusCode == 'UCOF'; },
            GetFormsByCourseApplication(courseApplication) { return this.forms.filter(f => f.academicSession == courseApplication.AcademicSession) },
            CanShowCourse(courseApplication) {
                let canShow = false;

                if (this.IsStudentOldEnough === false) canShow = false;
                else if (this.HasCourse(courseApplication) === false) canShow = false;
                else if (this.IsCourseApplicationValid(courseApplication) === false) canShow = true;
                else if (this.IsCourseApplicationCancelled(courseApplication) === true) canShow = true;
                else {
                    let forms = this.GetFormsByCourseApplication(courseApplication);

                    for (var i = 0; i < forms.length; i++) {
                        if (this.IsApplicationOpen(courseApplication, forms[i].type) === true) {
                            canShow = true;
                            break;
                        }
                        if (this.HasApplicationByCourseFormType(courseApplication, forms[i].type)) {
                            canShow = true;
                            break;
                        }
                    }
                }

                // console.log(
                //     'CanShowCourse::'
                //     , 'courseApplication:', courseApplication.CourseCode
                //     , 'academicSession:', courseApplication.AcademicSession
                //     , 'IsStudentOldEnough:', this.IsStudentOldEnough
                //     , 'HasCourse:', this.HasCourse(courseApplication)
                //     , 'IsCourseApplicationValid:', this.IsCourseApplicationValid(courseApplication)
                //     , 'IsCourseApplicationCancelled:', this.IsCourseApplicationCancelled(courseApplication)
                // );

                return canShow;
            },
            HasAppropriateCourseApplication(courseApplication) {
                return courseApplication.ApplicationStatus == 'UCOF' ||
                    courseApplication.IsOfferAccepted ||
                    courseApplication.IsUnconditionalOfferAccepted ||
                    courseApplication.IsPreRegistered ||
                    courseApplication.IsRegistered ||
                    courseApplication.IsCurrent;
            },

            GetBursaryApplicationOpenDate(courseCode) {
                return this.courses.find(c => c.courseCode == courseCode)?.bursaryApplicationStartDate;
            },
            GetBursaryApplicationCloseDate(courseCode) {
                return this.courses.find(c => c.courseCode == courseCode)?.bursaryApplicationEndDate;
            },
            GetFundingApplicationOpenDate(courseCode) {
                return this.courses.find(c => c.courseCode == courseCode)?.fundingApplicationStartDate;
            },
            GetFundingApplicationCloseDate(courseCode) {
                return this.courses.find(c => c.courseCode == courseCode)?.fundingApplicationEndDate;
            },
            GetFinancialAssistanceApplicationOpenDate(courseCode) {
                return this.courses.find(c => c.courseCode == courseCode)?.financialAssistanceApplicationStartDate;
            },
            GetFinancialAssistanceApplicationCloseDate(courseCode) {
                return this.courses.find(c => c.courseCode == courseCode)?.financialAssistanceApplicationEndDate;
            },
            GetApplicationOpenDatesLabel(courseApplication, form) {
                let today = moment().locale('en-gb');
                let openDate = moment().locale('en-gb');
                let closeDate = moment().locale('en-gb');

                switch (form.baseType) {
                    case 'Bursary':
                    case 'EMA':
                        openDate = moment(this.GetBursaryApplicationOpenDate(courseApplication.CourseCode)).locale('en-gb');
                        closeDate = moment(this.GetBursaryApplicationCloseDate(courseApplication.CourseCode)).locale('en-gb');
                        break;
                    case 'Childcare':
                    case 'Housing':
                        openDate = moment(this.GetFundingApplicationOpenDate(courseApplication.CourseCode)).locale('en-gb');
                        closeDate = moment(this.GetFundingApplicationCloseDate(courseApplication.CourseCode)).locale('en-gb');
                        break;
                    case 'FinancialAssistance':
                        openDate = moment(this.GetFinancialAssistanceApplicationOpenDate(courseApplication.CourseCode)).locale('en-gb');
                        closeDate = moment(this.GetFinancialAssistanceApplicationCloseDate(courseApplication.CourseCode)).locale('en-gb');
                        break;
                }

                if (today < openDate) return `${form.name} applications on this course will open on <strong>${this.$options.filters.formatLongDate(openDate)}</strong>`;
                if (today > closeDate) return `${form.name} applications on this course closed on <strong>${this.$options.filters.formatLongDate(closeDate)}</strong>`;
                else return `If you are applying for ${form.name} funding on this course, the application must be started between <strong>${this.$options.filters.formatLongDate(openDate)}</strong> and <strong>${this.$options.filters.formatLongDate(closeDate)}</strong>`;
            },
            HasApplicationDates(courseApplication, formType) {
                if (this.HasCourse(courseApplication) === false) return false;

                let openDate = moment().locale('en-gb');
                let closeDate = moment().locale('en-gb');

                switch (formType) {
                    case 'Bursary':
                    case 'EMA':
                        openDate = moment(this.GetBursaryApplicationOpenDate(courseApplication.CourseCode)).locale('en-gb');
                        closeDate = moment(this.GetBursaryApplicationCloseDate(courseApplication.CourseCode)).locale('en-gb');
                        break;
                    case 'FEChildcare':
                    case 'HEChildcare':
                    case 'HEHousing':
                        openDate = moment(this.GetFundingApplicationOpenDate(courseApplication.CourseCode)).locale('en-gb');
                        closeDate = moment(this.GetFundingApplicationCloseDate(courseApplication.CourseCode)).locale('en-gb');
                        break;
                    case 'FinancialAssistance':
                        openDate = moment(this.GetFinancialAssistanceApplicationOpenDate(courseApplication.CourseCode)).locale('en-gb');
                        closeDate = moment(this.GetFinancialAssistanceApplicationCloseDate(courseApplication.CourseCode)).locale('en-gb');
                        break;
                    default:
                        openDate = null;
                        closeDate = null;
                        break;
                }

                if (openDate?.isValid() === false || closeDate?.isValid() === false) return false;
                else return true;
            },
            IsApplicationOpen(courseApplication, formType) {
                if (this.HasCourse(courseApplication) === false) return false;
                if (this.IsCourseApplicationValid(courseApplication) === false) return false;
                if (this.IsCourseApplicationCancelled(courseApplication) === true) return false;

                let today = moment().locale('en-gb');
                let openDate = moment().locale('en-gb');
                let closeDate = moment().locale('en-gb');

                switch (formType) {
                    case 'Bursary':
                    case 'EMA':
                        openDate = moment(this.GetBursaryApplicationOpenDate(courseApplication.CourseCode)).locale('en-gb');
                        closeDate = moment(this.GetBursaryApplicationCloseDate(courseApplication.CourseCode)).locale('en-gb');
                        break;
                    case 'FEChildcare':
                    case 'HEChildcare':
                    case 'HEHousing':
                        openDate = moment(this.GetFundingApplicationOpenDate(courseApplication.CourseCode)).locale('en-gb');
                        closeDate = moment(this.GetFundingApplicationCloseDate(courseApplication.CourseCode)).locale('en-gb');
                        break;
                    case 'FinancialAssistance':
                        openDate = moment(this.GetFinancialAssistanceApplicationOpenDate(courseApplication.CourseCode)).locale('en-gb');
                        closeDate = moment(this.GetFinancialAssistanceApplicationCloseDate(courseApplication.CourseCode)).locale('en-gb');
                        break;
                    default:
                        openDate = null;
                        closeDate = null;
                        break;
                }

                // console.log(courseApplication.CourseCode, formType, openDate, today < openDate, today > closeDate);

                if (openDate?.isValid() === false || closeDate?.isValid() === false) return false;
                else if (today < openDate) return false;
                else if (today > closeDate) return false;
                else return true;
            },
            HasApplicationByAcademicSessionFormType(academicSession, type) {
                return this.ActiveApplications.filter(a => a.academicSession == academicSession).some(a => a.formType == type);
            },
            GetApplicationsByCourseFormType(courseCode, formType) {
                return this.ActiveApplications.filter(a => a.courseCode == courseCode && a.formType == formType);
            },
            HasApplicationByCourseFormType(courseCode, type) {
                return this.ActiveApplications.some(a => a.courseCode == courseCode && a.formType == type);
            },
            HasSubmittedApplicationByFormType(formType) {
                return this.SubmittedApplications.some(a => a.formType == formType);
            },
            HasActiveApplicationByCourseFormType(courseCode, type) {
                return this.ActiveApplications.some(a => a.courseCode == courseCode && a.formType == type);
            },
            HasSubmittedApplicationByCourseFormType(courseCode, type) {
                return this.SubmittedApplications.some(a => a.courseCode == courseCode && a.formType == type);
            },
            GetPrimaryApplication(courseCode) {
                return this.ActiveApplications.find(a => a.courseCode == courseCode && ['Bursary', 'EMA'].includes(a.formType));
            },
            IsAsylumSeeker(courseCode) {
                return this.GetPrimaryApplication(courseCode)?.isAsylumSeeker || false;
            },
            GetCourseApplicationByCourseFormType(courseApplication, formType) {
                // console.log(
                //     'GetCourseApplicationByCourseFormType::'
                //     , 'courseApplication:', courseApplication.CourseCode
                //     , 'academicSession:', courseApplication.AcademicSession
                //     , 'formType:', formType
                //     , 'AgeAtCourseStart:', courseApplication.AgeAtCourseStart
                //     , 'HE_FE:', courseApplication.HE_FE
                //     , 'HasApplicationByAcademicSessionFormType:', this.HasApplicationByAcademicSessionFormType(courseApplication.AcademicSession, formType)
                //     , 'application:', this.applications.filter(a => a.formType == formType)
                // );

                switch (formType) {
                    case 'Bursary':
                        if (courseApplication.AgeAtCourseStart >= 18 && // Applicant must be 18 or over
                            courseApplication.HE_FE == 'FE' && // Course must be FE
                            this.HasApplicationByAcademicSessionFormType(courseApplication.AcademicSession, formType) === false) { // Applicant must not have a bursary application on the same course
                            return courseApplication;
                        }
                        return null;
                    case 'EMA':
                        if (courseApplication.AgeAtCourseStart < 18 && // Applicant must be under 18
                            courseApplication.HE_FE == 'FE' && // Course must be FE
                            this.HasApplicationByAcademicSessionFormType(courseApplication.AcademicSession, formType) === false) { // Applicant must not have a EMA application on the same course
                            return courseApplication;
                        }
                        return null;
                    case 'FEChildcare':
                        if (courseApplication.AgeAtCourseStart >= 18 && // Applicant must be 18 or over
                            courseApplication.HE_FE == 'FE' && // Course must be FE
                            this.IsAsylumSeeker(courseApplication.CourseCode) === false && // Applicant cannot be an asylum seeker
                            (this.HasSubmittedApplicationByCourseFormType(courseApplication.CourseCode, 'Bursary') === true ||
                            this.HasSubmittedApplicationByCourseFormType(courseApplication.CourseCode, 'EMA') === true) && // Applicant must have a Bursary or EMA application on the same course
                            this.HasApplicationByAcademicSessionFormType(courseApplication.AcademicSession, formType) === false) { // Applicant must not have a FE childcare application on the same course
                            return courseApplication;
                        }
                        return null;
                    case 'HEChildcare':
                        if (courseApplication.HE_FE == 'HE' && // Course must be HE
                            this.IsAsylumSeeker(courseApplication.CourseCode) === false && // Applicant cannot be an asylum seeker
                            this.HasAppropriateCourseApplication(courseApplication) && // Applicant must have application status of one of the above
                            this.HasActiveApplicationByCourseFormType(courseApplication.CourseCode, 'EMA') === false && // Applicant must not have a EMA application on the same course
                            this.HasApplicationByAcademicSessionFormType(courseApplication.AcademicSession, formType) === false) { // Applicant must not have a HE childcare application on the same course
                            return courseApplication;
                        }
                        return null;
                    case 'HEHousing':
                        if (courseApplication.HE_FE == 'HE' && // Course must be HE
                            this.IsAsylumSeeker(courseApplication.CourseCode) === false && // Applicant cannot be an asylum seeker
                            this.HasAppropriateCourseApplication(courseApplication) && // Applicant must have application status of one of the above
                            this.HasActiveApplicationByCourseFormType(courseApplication.CourseCode, 'EMA') === false && // Applicant must not have a EMA application on the same course
                            this.HasApplicationByAcademicSessionFormType(courseApplication.AcademicSession, formType) === false) { // Applicant must not have a HE housing application on the same course
                            return courseApplication;
                        }
                        return null;
                    case 'FinancialAssistance':
                        // Applicant can apply for FA multiple times
                        if (courseApplication.AgeAtCourseStart >= 18 && // Applicant must be 18 or over
                            this.IsAsylumSeeker(courseApplication.CourseCode) === false) { // Applicant cannot be an asylum seeker
                            return courseApplication;
                        }
                        return null;
                    default: // Catch-all for new form types
                        return null;
                }
            },
            CanShowForm(courseApplication, formType) {
                // console.log(
                //     'CanShowForm::'
                //     , 'courseApplication:', courseApplication.CourseCode
                //     , 'formType:', formType
                //     , 'IsCourseApplicationCancelled:', this.IsCourseApplicationCancelled(courseApplication)
                //     , 'IsCourseApplicationValid:', this.IsCourseApplicationValid(courseApplication)
                //     , 'CanApply:', this.CanApply(courseApplication, formType)
                //     , 'HasApplicationByCourseFormType:', this.HasApplicationByCourseFormType(courseApplication.CourseCode, formType)
                // );

                return this.IsCourseApplicationCancelled(courseApplication) === false &&
                    this.IsCourseApplicationValid(courseApplication) === true &&
                    (this.CanApply(courseApplication, formType) === true ||
                    this.HasApplicationByCourseFormType(courseApplication.CourseCode, formType) === true);
            },
            CanApply(courseApplication, formType) {
                // console.log(
                //     'CanApply::'
                //     , 'courseApplication:', courseApplication.CourseCode
                //     , 'formType:', formType
                //     , 'IsStudentOldEnough:', this.IsStudentOldEnough
                //     , 'IsCourseApplicationCancelled:', this.IsCourseApplicationCancelled(courseApplication)
                //     , 'IsCourseApplicationValid:', this.IsCourseApplicationValid(courseApplication)
                //     , 'IsApplicationOpen:', this.IsApplicationOpen(courseApplication, formType)
                //     , 'GetCourseApplicationByCourseFormType:', this.GetCourseApplicationByCourseFormType(courseApplication, formType)
                // );

                return this.IsStudentOldEnough &&
                    this.IsCourseApplicationCancelled(courseApplication) === false &&
                    this.IsCourseApplicationValid(courseApplication) === true &&
                    this.IsApplicationOpen(courseApplication, formType) &&
                    this.GetCourseApplicationByCourseFormType(courseApplication, formType) != null;
            },
            IsInactiveApplication(application) {
                return ['Cancelled', 'Funding Not Requested'].includes(application.status);
            },
            IsApplicationUnsubmitted(application) {
                return ['Unsubmitted'].includes(application.status);
            },
            OpenApplicationLabel(application) {
                return this.IsApplicationUnsubmitted(application) ? 'Open' : 'View';
            },
            OpenNewApplication(courseApplication, formType) {
                this.CtrlOpen(`/course/${courseApplication.CourseCode}/${encodeURIComponent(courseApplication.CourseName)}/form/session/${courseApplication.AcademicSession}/type/${formType}`);
            },
            OpenApplication(application, courseApplication) {
                if (this.IsCourseApplicationCancelled(courseApplication) === false) {
                    this.CtrlOpen(`/form/session/${application.academicSession}/type/${application.formType}/application/${application.id}/page/${application.lastPageAccessed}`);
                }
            },
            ConfirmDeleteApplication(application) {
                eventBus.$emit('confirm-dialog:confirm-delete-application:show', application);
            },
            HasEligibleEMABursary(application) {
                let bursary = this.bursaries.find(b => b.id == application.fkBursaryID);

                if (bursary != null && bursary.type == 'EMA' && bursary.emaQualify === true) return true;
                else return false;
            },
            OpenBursary(bursary) {
                this.CtrlOpen(`/bursary/${bursary.id}`);
            },
            OpenCommunication(communication) {
                this.CtrlOpen(`/inbox/communication/${communication.id}`);
            },
            UpdateLearningAgreement(application) {
                this.SaveBursaryLearningAgreement(application.fkBursaryID, application.learningAgreement);
            },
            GetTotalPayAward(bursary) {
                return this.bursaryFundingPaymentDetails.filter(bfpd => bfpd.fkBursaryID == bursary.id).reduce(
                    (accumulator, funding) => accumulator + funding.totalAmount, bursary.payAward,
                );
            },
        }
    }
</script>
